import {
  IApplyContractTermChange,
  IApplyContractTermDocuments,
  IApplyContractTermRequest,
  IApplyContractTermResult,
  IApplyContractTermTarget,
} from "../../../store/queries/contractModelDrafts";
import { CONTRACT_MODEL } from "../copy";

/**
 * CLE-2811 — how the "save to contract" preview labels each field. Pure, so the wording the reviewer
 * relies on ("will replace" vs "will fill") is tested without a DOM.
 */

export interface IApplyActionLabel {
  label: string;
  severity: "success" | "info" | "warning" | "danger";
}

export const APPLY_ACTION_LABELS: Record<
  IApplyContractTermChange["action"],
  IApplyActionLabel
> = {
  filled: { label: "Will fill", severity: "success" },
  // Overwrites something a person may have typed — the one label that must stand out.
  replaced: { label: "Will replace", severity: "danger" },
  appended: { label: "Will append", severity: "info" },
  skipped: { label: "Left as is", severity: "warning" },
};

/** The preview's one-line summary: what the save would do, by kind, in a fixed order. */
export const summarizeApplyPlan = (
  changes: IApplyContractTermChange[]
): string => {
  const count = (a: IApplyContractTermChange["action"]) =>
    changes.filter((c) => c.action === a).length;
  const parts = [
    [count("filled"), "will fill"],
    [count("replaced"), "will replace"],
    [count("appended"), "will append"],
    [count("skipped"), "left as is"],
  ]
    .filter(([n]) => (n as number) > 0)
    .map(([n, what]) => `${n} ${what}`);
  return parts.length > 0 ? parts.join(" · ") : "Nothing to save";
};

/** True when the save would write anything at all — the button is disabled otherwise. */
export const planWritesSomething = (
  changes: IApplyContractTermChange[]
): boolean => changes.some((c) => c.action !== "skipped");

// ---------------------------------------------------------------------------------------------
// CLE-2811 — where the save goes. One document often prices several payers (Medflex, SuperMed, ACA),
// one contract term each, and a run belongs to ONE contract. The server resolves the target
// (`target` on the dry run); these turn its answer into what the panel says and sends.
// ---------------------------------------------------------------------------------------------

/**
 * `default`: the term has a home without asking (the run's contract, or a standalone run's new draft).
 * `pick`: an off-payer term with existing contracts for its payer — she chooses one.
 * `create`: an off-payer term with none — she can create a draft for its payer.
 */
export type ApplyTargetChoice = "default" | "pick" | "create";

export interface IApplyTargetView {
  /** "This term is for SuperMed, not the contract being modeled." Null when there is nothing to explain. */
  notice: string | null;
  choice: ApplyTargetChoice;
  /** The existing contracts she can pick from, labelled with their status. Empty unless `pick`. */
  options: { label: string; value: string }[];
  /** Preselected in the picker: the server's proposal when it is one of the options, else the first. */
  initialContractId: string | null;
}

export const describeApplyTarget = (
  target: IApplyContractTermTarget,
  runHasContract: boolean
): IApplyTargetView => {
  if (!target.requiresChoice) {
    return {
      notice: null,
      choice: "default",
      options: [],
      initialContractId: null,
    };
  }
  const notice = runHasContract
    ? `This term is for ${target.payerName}, not the contract being modeled.`
    : `This term is for ${target.payerName}, not the payer being modeled.`;
  const options = target.existingCandidates.map((c) => ({
    value: c.id,
    label: `${c.name} · ${c.status}`,
  }));
  if (options.length === 0) {
    return { notice, choice: "create", options, initialContractId: null };
  }
  const proposed = options.some((o) => o.value === target.contractFeeScheduleId)
    ? target.contractFeeScheduleId
    : null;
  return {
    notice,
    choice: "pick",
    options,
    initialContractId: proposed ?? options[0].value,
  };
};

/**
 * What the request must carry for the choice. `default` sends nothing (the server knows the home); an
 * off-payer save ALWAYS names its target — the server refuses one that does not.
 */
export const applyTargetRequest = (
  choice: ApplyTargetChoice,
  selectedContractId: string | null
): Pick<
  IApplyContractTermRequest,
  "targetContractFeeScheduleId" | "createNewContract"
> => {
  if (choice === "pick" && selectedContractId) {
    return { targetContractFeeScheduleId: selectedContractId };
  }
  if (choice === "create") return { createNewContract: true };
  return {};
};

/** The save button: where it writes, in words. */
export const saveButtonLabel = (
  target: IApplyContractTermTarget,
  choice: ApplyTargetChoice,
  selectedContractName: string | null
): string => {
  if (choice === "pick") {
    return selectedContractName
      ? `Save to ${selectedContractName}`
      : "Save to contract";
  }
  if (choice === "create") {
    return `Create draft contract for ${target.payerName}`;
  }
  return target.mode === "create"
    ? "Create draft contract"
    : "Save to contract";
};

const documentCount = (n: number): string =>
  `${n} document${n === 1 ? "" : "s"}`;

/**
 * The one line about the run's documents. A NEW contract gets copies of them (its own files); an
 * existing contract never does, and an off-payer save says so, since she might expect otherwise.
 * `saved` = after the real save (what happened) rather than on the preview (what will).
 */
export const documentsLine = (
  target: IApplyContractTermTarget,
  documents: IApplyContractTermDocuments | null,
  saved: boolean
): string | null => {
  if (!documents) {
    return target.requiresChoice
      ? `This ${CONTRACT_MODEL}'s documents are not copied to an existing contract.`
      : null;
  }
  const { copied, notCopied } = documents;
  if (copied.length === 0 && notCopied.length === 0) {
    return `This ${CONTRACT_MODEL} has no documents to copy to the new contract.`;
  }
  const skipped =
    notCopied.length > 0
      ? ` Not copied: ${notCopied.join(", ")}${
          saved ? " — upload them on the contract page." : "."
        }`
      : "";
  if (copied.length === 0) return skipped.trim();
  return saved
    ? `${documentCount(copied.length)} copied to the new contract.${skipped}`
    : `This ${CONTRACT_MODEL}'s ${documentCount(
        copied.length
      )} will be copied to the new contract.${skipped}`;
};

/** The success message after a real save. */
export const savedMessage = (result: IApplyContractTermResult): string => {
  const { target } = result;
  if (result.created) {
    return `A draft contract was created for ${target.payerName}. Review and activate it in Payer Rules.`;
  }
  if (target.mode === "create") {
    // Idempotent: this term already created a contract, and asking again returns it.
    return `This term already created a draft contract for ${target.payerName} — nothing new was created.`;
  }
  const where =
    target.mode === "other_contract" && target.contractName
      ? target.contractName
      : "the contract";
  if (!result.applied) {
    return `Nothing to save — ${where} already had every field the agent found.`;
  }
  const replaced = result.changes.filter((c) => c.action === "replaced").length;
  if (replaced > 0) {
    return `Saved to ${where}. ${replaced} text field${
      replaced === 1 ? " was" : "s were"
    } replaced; everything else that already had a value was left alone.`;
  }
  return `Saved to ${where}. Fields that already had a value were left alone.`;
};
